// Open a client environment in this tab (#10718 slice 1b, slice 3), as the
// staff member.
//
// `/client-environment/open?org=<id>` opens that org and sends the tab into
// it; without `org` it goes to the admin console, the one place that lists
// clients. This page always runs at home, even in a tab marked for a client,
// because opening needs the staff member's own session. The ended page's
// Reopen lands here. Only a click this tab made opens on its own (it arms
// `armAutoOpen` first): any other link takes a click here, since opening
// writes an access record the client sees (review 1b M1).
//
// `?org=<id>&elevate=1` is where "Admin access" lands: a reason, then the
// staff member's own session elevates the open grant and the tab goes back.
// The client environment's token cannot elevate itself (review 1b M2).
import React, { useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck } from "@phosphor-icons/react";
import fpFetch from "@/services/fpFetch";
import {
  clientEnvironmentEnterUrl,
  holdsTokenFor,
  markTabForClient,
  safeNextPath,
  STAFF_CONSOLE_PATH,
  takeAutoOpen,
} from "@/services/clientEnvironment";
import {
  CURRENT_GRANT_URL,
  GRANTS_URL,
  type StaffGrant,
} from "@/hooks/useStaffGrant";
import { BASE_API_URL_V2 } from "@/constants";

interface OrganizationSummary {
  _id: string;
  name: string;
}

/** Every org's id and name, for naming the one in the link. */
function useOrganizations() {
  return useQuery<OrganizationSummary[]>({
    queryKey: ["staff-grants", "organizations"],
    queryFn: async () => {
      const response = await fpFetch(
        `${BASE_API_URL_V2}/api/v2/admin/organizations`
      );
      if (!response?.ok) throw new Error("organizations unavailable");
      const body = (await response.json()) as {
        organizations?: OrganizationSummary[];
      };
      return (body.organizations ?? []).map((org) => ({
        _id: org._id,
        name: org.name,
      }));
    },
  });
}

const go = (url: string): void => {
  window.location.replace(url);
};

/** Open the grant and return the client environment's entry link. */
async function openGrant(organizationId: string, next: string | null) {
  const response = await fpFetch(GRANTS_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ organization_id: organizationId }),
  });
  if (!response?.ok) throw new Error("open failed");
  const body = (await response.json()) as { exchange_code: string };
  return clientEnvironmentEnterUrl(body.exchange_code, next);
}

interface ClientEnvironmentOpenProps {
  /** Sends the tab to the client environment; replaced in stories. */
  onOpened?: (url: string) => void;
}

const Card: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <main className="min-h-[100dvh] w-full flex items-start md:items-center justify-center bg-white px-4 py-8">
    <div className="w-full max-w-md rounded-[10px] border border-gray-200 p-4 md:p-6 flex flex-col gap-3">
      <div className="flex items-center gap-2 text-[#0B5F5A]">
        <ShieldCheck className="w-5 h-5 flex-shrink-0" weight="fill" />
        <span className="text-xs font-medium uppercase tracking-wide">
          Client environment
        </span>
      </div>
      <h1 className="text-lg font-semibold text-[#10141B] break-words">
        {title}
      </h1>
      {children}
    </div>
  </main>
);

const ClientEnvironmentOpen: React.FC<ClientEnvironmentOpenProps> = ({
  onOpened = go,
}) => {
  const [params] = useSearchParams();
  const staff = useSelector((state: { userData?: { isStaff?: boolean } }) =>
    Boolean(state.userData?.isStaff)
  );
  const openGrant = useSelector(
    (state: { userData?: { staffGrant?: StaffGrant | null } }) =>
      state.userData?.staffGrant ?? null
  );
  const org = params.get("org");
  const next = safeNextPath(params.get("next"));
  // The client is already open and this browser holds its grant's token (a
  // record link from the console, opened in a new tab): join it, drawing no
  // new code and writing no access record.
  const joins =
    org !== null &&
    params.get("elevate") !== "1" &&
    openGrant?.organizationId === org &&
    holdsTokenFor(openGrant.grantId);

  if (!staff) {
    return (
      <Card title="Open a client">
        <p className="text-sm text-[#6E7480]">
          Only Finpilot staff can open a client environment.
        </p>
      </Card>
    );
  }
  if (org === null) {
    return <Navigate to={STAFF_CONSOLE_PATH} replace />;
  }
  if (joins) {
    return <JoinOpenClient next={next} onJoined={onOpened} />;
  }
  if (params.get("elevate") === "1") {
    return <TakeAdminAccess org={org} next={next} onDone={onOpened} />;
  }
  return <OpenClient org={org} next={next} onOpened={onOpened} />;
};

const JoinOpenClient: React.FC<{
  next: string | null;
  onJoined: (url: string) => void;
}> = ({ next, onJoined }) => {
  React.useEffect(() => {
    markTabForClient();
    onJoined(next ?? "/");
  }, [next, onJoined]);
  return (
    <Card title="Open a client">
      <p className="text-sm text-[#6E7480]" aria-busy>
        Opening…
      </p>
    </Card>
  );
};

const OpenClient: React.FC<{
  org: string;
  next: string | null;
  onOpened: (url: string) => void;
}> = ({ org, next, onOpened }) => {
  // Opens on its own only when this tab's own click armed it for this org,
  // read once for the life of the page.
  const [autoOpen] = useState<boolean>(() => takeAutoOpen(org));
  // Each click is a new attempt, so trying again after a refusal asks again.
  const [attempt, setAttempt] = useState<number>(0);
  const organizations = useOrganizations();
  const asked = autoOpen || attempt > 0;

  // A read keyed by the org and the attempt, like the entry page's exchange:
  // a remount shares the one request instead of opening the grant twice.
  const opening = useQuery<string, Error>({
    queryKey: ["client-environment-open", org, next, attempt],
    queryFn: () => openGrant(org, next),
    enabled: asked,
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  React.useEffect(() => {
    if (opening.data) onOpened(opening.data);
  }, [opening.data, onOpened]);

  const failure =
    opening.error === null ? null : "Could not open that client. Try again.";

  if (asked && failure === null) {
    return (
      <Card title="Open a client">
        <p className="text-sm text-[#6E7480]" aria-busy>
          Opening…
        </p>
      </Card>
    );
  }
  const named = organizations.data?.find((o) => o._id === org)?.name ?? null;
  return (
    <Card title="Open a client">
      <button
        type="button"
        onClick={() => setAttempt(attempt + 1)}
        className="self-start text-sm px-3 py-2 rounded-md bg-gray-800 text-white hover:bg-gray-700 break-words cursor-pointer"
      >
        Open {named ?? "this client"}
      </button>
      {failure !== null && (
        <p role="alert" className="text-xs text-red-600 break-words">
          {failure}
        </p>
      )}
    </Card>
  );
};

const TakeAdminAccess: React.FC<{
  org: string;
  next: string | null;
  onDone: (url: string) => void;
}> = ({ org, next, onDone }) => {
  const organizations = useOrganizations();
  const [reason, setReason] = useState<string>("");
  const [busy, setBusy] = useState<boolean>(false);
  const [problem, setProblem] = useState<string | null>(null);
  const named = organizations.data?.find((o) => o._id === org)?.name ?? null;

  const submit = async (): Promise<void> => {
    setBusy(true);
    setProblem(null);
    const response = await fpFetch(`${CURRENT_GRANT_URL}/elevate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason: reason.trim(), organization_id: org }),
    });
    setBusy(false);
    if (response?.ok) {
      onDone(next ?? "/");
      return;
    }
    setProblem(
      response?.status === 409
        ? "A different client is open now."
        : response?.status === 404
          ? "This client environment is no longer open."
          : "Could not take admin access."
    );
  };

  return (
    <Card title={named ? `Admin access in ${named}` : "Admin access"}>
      <form
        className="flex flex-col gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          autoFocus
          aria-label="Reason for admin access"
          placeholder="Reason"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:border-gray-500 focus:outline-none"
        />
        {problem !== null && (
          <p role="alert" className="text-xs text-red-600 break-words">
            {problem}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="submit"
            disabled={busy || reason.trim() === ""}
            className="text-sm px-3 py-2 rounded-md bg-gray-800 text-white hover:bg-gray-700 disabled:opacity-50 cursor-pointer"
          >
            Take admin access
          </button>
          <a
            href={next ?? "/"}
            className="text-sm px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </a>
        </div>
      </form>
    </Card>
  );
};

export default ClientEnvironmentOpen;
